/**
 * 卡墙规格单源(2026-07-25 G1 铺量批)。
 *
 * ═══ 为什么要有这个文件 ═══════════════════════════════════════════════════
 * 卡片体系重构落了三类卡,但**规格散在各自的组件里**,于是同一个"卡"在不同页长得不一样:
 *   - 场景卡 `SceneCapCard` 圆角 `calc(--radius + 4px)` → standard 12 / cyber 16 / comic 24
 *   - 部门卡 `.wb-card`     圆角 `calc(--radius + 4px)` → 同上
 *   - 作品卡 `WorkCard`     圆角 `calc(--radius * 1.5)` → standard 12 / cyber 18 / comic **30**
 * standard 皮肤下两者都是 12px,**肉眼与截图都看不出差别**;只有 cyber / comic 才裂开。
 * 这是「三皮肤全保」下的典型隐性不一致 —— 必须收成一个常量,而不是三处各写各的。
 *
 * 取 `* 1.5` 不取 `+ 4px` 的理由:
 *   ① 拍板过的形状锁是「`calc(--radius × n)`,**零写死 px**」,`+ 4px` 本身就违规;
 *   ② 比例式才跟得住皮肤的圆角语言 —— comic 基准 20px 要的是更圆的卡(30),
 *      `+4px` 把它压成 24,和该皮肤其余圆角对不上;
 *   ③ 作品卡的 `* 1.5` 已有单测锁死,改它的成本高于改另外两类。
 *
 * ═══ 三档密度,不是一档 ═══════════════════════════════════════════════════
 * 「卡宽 250~365」是**能力卡**的判据,不能无差别套到所有卡墙上:
 *   - `cap`   能力/富媒体档 250~365:场景卡、部门卡、样片卡、视频模板卡。
 *             卡面要放 3 层文案 + 形制图示 +(样片)播放器 + 整行按钮,窄于 250 就开始换行塌。
 *   - `quad`  固定张数档:平台首页 KPI 行这类**恒 N 张**的指标行,只取约数列,永不空半行。
 *   - `media` 缩略图/产物档 150~260:作品卡、海报模板卡。
 *             它们的卡面主体是一张图,**浏览量大于阅读量** —— 硬套 250~365 会把海报模板库
 *             从一行 6 张压到一行 4 张,是把一个"挑模板"的页面改差。
 * 三档的阈值分别在 `scene-card.css`(cap)/ `work-card.css`(media、quad)里,
 * 都按「n 列要保住下限所需的最小容器」倒推,不是从视口断点换算。本文件只做命名与选用。
 *
 * ★ 一律容器查询,永不视口断点:列宽取决于**容器**(= 视口 − 侧栏 − 内距),与视口无关。
 *   本轮四个页面在视口 780 处同时塌陷(容器 476~496 却已越过 md/lg 断点),
 *   海报模板库更是被切成 5 列 **87px**,就是量错对象的老账。
 */

/** 卡墙外层:开容器查询上下文。★网格本身不能是容器,必须套在它外面一层。 */
export const CARD_WALL = "cardwall";

/** 能力/富媒体档栅格 250~365(与 `.scene-grid` 同阈值)。 */
export const CARD_WALL_CAP = "cardwall-cap";

/** 缩略图/产物档栅格 150~260(与 `.work-grid` 同阈值)。 */
export const CARD_WALL_MEDIA = "cardwall-media";

/**
 * 固定张数的指标行栅格,只取张数的**约数**列(1/2/4,含 2/3 张的数量查询)。
 * cap / media 两档都是 auto 列数,4 张卡落进 5~6 列的网格会空出半行。
 */
export const CARD_WALL_QUAD = "cardwall-quad";

/**
 * 五类卡的形状锁。★类名必须是**字面量**才会被 Tailwind JIT 扫到
 * (`tailwind.config.ts` 的 content 已含 `src/**\/*.ts`),不要拼字符串。
 */
export const CARD_WALL_RADIUS = "rounded-[calc(var(--radius)*1.5)]";

/**
 * 卡墙里**整张卡可点**时必须带的一组属性(2026-07-25 门 H-D 实测)。
 *
 * ★为什么要收成一个函数:`<div onClick>` / `<Card onClick>` 鼠标点得到,但键盘 Tab
 *   **永远到不了** —— 实测视频模板卡与平台 KPI 卡都是这样(参照的场景卡做对了:
 *   role=button + tabIndex=0 + Enter/Space)。这三件缺一不可,分散在各页写迟早漏一件。
 *
 * ★不用 `<button>` 包整张卡的原因:卡面里还有别的可聚焦件(链接、次级按钮),
 *   button 里嵌 button 是非法 HTML,读屏会把嵌套内容吞掉。
 */
export function clickableCardProps(onOpen: () => void) {
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (e: { key: string; preventDefault: () => void }) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

/** 卡墙主操作按钮的最小触控高度(44px,ui-ux-pro-max Touch Target Size)。 */
export const CARD_WALL_TOUCH = "min-h-11";
